import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { nodes } from "@/config/nodes";
import { AppContextType } from "@/context/app";
import { useGithubRepos } from "@/hooks/use-github-repos";
import { SiGithub } from "react-icons/si";

type Template = {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
};

export const AppPopoverComponent = ({
  idx,
  app,
  handleAppEdit,
  ...field
}: {
  idx: number;
  app: AppContextType;
  handleAppEdit: (index: number, app: AppContextType) => void;
}) => {
  const [open, setOpen] = React.useState(false);
  const { repos, loading } = useGithubRepos();

  const baseTemplates = (app.type ? nodes[app.type]?.templates : []) || [];
  
  const templates = React.useMemo(() => {
    if (app.type === 'custom-repo') {
      const repoTemplates = repos.map(repo => ({
        value: repo.full_name,
        label: repo.full_name,
        icon: <SiGithub className="w-4 text-[#181717] dark:text-white" />,
      }));
      return [...baseTemplates, ...repoTemplates];
    }
    return baseTemplates;
  }, [app.type, baseTemplates, repos]);

  const activeValue = (app.type === 'custom-repo' && app.template === 'github-repo' && app.config?.githubRepo) 
    ? app.config.githubRepo 
    : (app.type === 'custom-url' && app.template === 'github-url' && app.config?.githubUrl)
    ? app.config.githubUrl
    : app.template;

  const selectedTemplate = templates?.find(
    (template: Template) => template.value === activeValue,
  );

  const displayLabel = (app.type === 'custom-repo' && app.template === 'github-repo' && app.config?.githubRepo)
    ? app.config.githubRepo
    : (app.type === 'custom-url' && app.template === 'github-url' && app.config?.githubUrl)
    ? app.config.githubUrl
    : selectedTemplate?.label;

  const displayIcon = (app.type === 'custom-repo' && app.template === 'github-repo' && app.config?.githubRepo)
    ? <SiGithub className="w-4 text-[#181717] dark:text-white" />
    : (app.type === 'custom-url' && app.template === 'github-url' && app.config?.githubUrl)
    ? <SiGithub className="w-4 text-[#181717] dark:text-white" />
    : selectedTemplate?.icon;

  // For custom-url type, show an input field instead of dropdown
  if (app.type === 'custom-url') {
    return (
      <input
        type="text"
        value={app.config?.githubUrl || ''}
        onChange={(e) => {
          handleAppEdit(idx, {
            ...app,
            template: 'github-url',
            config: {
              ...app.config,
              githubUrl: e.target.value
            }
          });
        }}
        placeholder="Enter GitHub URL (e.g., https://github.com/user/repo)"
        className="flex w-full min-w-[200px] rounded-md border-2 border-primary bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        {...field}
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true} {...field}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="flex w-full min-w-[200px] cursor-pointer items-center justify-between rounded-md border-2 border-primary bg-background px-3 py-2 text-sm shadow-xs hover:bg-secondary"
        >
          <div className="flex items-center gap-2 truncate">
            {displayIcon}
            <span className="truncate">
              {displayLabel || "Select Starter Template"}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        avoidCollisions={true}
        className="min-w-[--radix-popover-trigger-width] p-0"
        sideOffset={5}
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search Templates" className="h-9" />
          <CommandList>
            <CommandEmpty>
              {loading && app.type === 'custom-repo' ? "Fetching repositories..." : "No template found."}
            </CommandEmpty>
            <CommandGroup>
              {templates?.map((template: Template) => (
                <CommandItem
                  key={template.value}
                  value={template.value}
                  disabled={template.disabled}
                  onSelect={() => {
                    if (template.disabled) return;
                    
                    const actualValue = template.value;
                    const isRepo = app.type === 'custom-repo' && actualValue.includes('/');
                    const isUrl = app.type === 'custom-url' && actualValue === 'github-url';
                    
                    const isAlreadySelected = isRepo 
                      ? (app.template === 'github-repo' && app.config?.githubRepo === actualValue)
                      : isUrl
                      ? (app.template === 'github-url')
                      : (app.template === actualValue);

                    if (isAlreadySelected) {
                      setOpen(false);
                      return;
                    }

                    if (isRepo) {
                      // Select Repo
                      handleAppEdit(idx, {
                        ...app,
                        template: 'github-repo',
                        config: {
                          ...app.config,
                          githubRepo: actualValue
                        }
                      });
                    } else if (isUrl) {
                      // For custom-url, we keep the current URL or set empty
                      handleAppEdit(idx, {
                        ...app,
                        template: 'github-url',
                        config: {
                          ...app.config,
                          githubUrl: app.config?.githubUrl || ''
                        }
                      });
                    } else {
                      // Select normal template
                      handleAppEdit(idx, {
                        ...app,
                        template: actualValue as AppContextType["template"],
                        config: {
                          ...app.config,
                          githubRepo: undefined,
                          githubUrl: undefined
                        }
                      });
                    }
                    setOpen(false);
                  }}
                  className={cn(
                    "flex items-center gap-2",
                    template.disabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-center gap-2">
                      {template.icon}
                      <span>{template.label}</span>
                      {template.disabled && (
                        <span className="rounded-sm bg-secondary px-1 text-[10px] font-bold text-muted-foreground uppercase">
                          Coming Soon
                        </span>
                      )}
                    </div>
                  </div>
                  {!template.disabled && (
                    <Check
                      className={cn(
                        "ml-auto",
                        activeValue === template.value
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
